import { useEffect, useState } from 'react';
import { InfoTip } from './InfoTip';
import { PANEL_INFO } from '../panelInfo';
import { PortfolioHealth } from './PortfolioHealth';
import { openTickerChart } from '../tickerChart';
import { OrderTicket } from './OrderTicket';

interface Position {
  account: string;
  ticker: string;
  secType: string;
  currency: string;
  position: number;
  avgCost: number;
  mark: number | null;
  marketValue: number | null;
  unrealizedPnL: number | null;
  unrealizedPnLPct: number | null;
  dayChangePct: number | null;
}

interface AccountSummary {
  netLiquidation: number | null;
  totalCash: number | null;
  buyingPower: number | null;
}

interface PositionsData {
  configured: boolean;
  connected: boolean;
  positions: Position[];
  totalValue?: number;
  totalPnL?: number;
  summary?: AccountSummary | null;
}

function money(n: number | null): string {
  if (n === null) return '—';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function pnlColor(n: number | null): string {
  if (n === null) return 'var(--text-dim)';
  return n >= 0 ? 'var(--green)' : 'var(--red)';
}

export function Positions() {
  const [data, setData] = useState<PositionsData | null>(null);
  const [ticket, setTicket] = useState<{ p: Position; action: 'BUY' | 'SELL' } | null>(null);

  useEffect(() => {
    const load = async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch('/api/positions', { signal: controller.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        setData(await response.json());
      } catch {
        /* keep previous state; next poll retries */
      } finally {
        clearTimeout(timeout);
      }
    };
    load();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, []);

  // Not configured (e.g. prod, or no IBKR_PORT in .env) → no panel at all
  if (!data || !data.configured) return null;

  return (
    <div className="panel positions-section" id="positions">
      <div className="panel-header">
        <div className="panel-title">
          <h2>Positions</h2>
          <InfoTip paragraphs={PANEL_INFO.positions} />
        </div>
        {data.connected ? (
          <span className="badge">
            {money(data.totalValue ?? null)} ·{' '}
            <span style={{ color: pnlColor(data.totalPnL ?? null) }}>
              {data.totalPnL !== undefined && data.totalPnL >= 0 ? '+' : ''}
              {money(data.totalPnL ?? null)}
            </span>
          </span>
        ) : (
          <span className="badge badge-offline">IB Gateway offline</span>
        )}
      </div>

      {!data.connected ? (
        <div className="empty-state">
          Start IB Gateway (logged in, API enabled) — the panel reconnects automatically.
        </div>
      ) : data.positions.length === 0 ? (
        <div className="empty-state">No open positions</div>
      ) : (
        <>
        <PortfolioHealth positions={data.positions} summary={data.summary ?? null} />
        <div className="table-container">
          <table className="screener-table">
            <thead>
              <tr>
                <th>Ticker</th>
                <th>Qty</th>
                <th>Avg Cost</th>
                <th>Mark</th>
                <th>Value</th>
                <th>Day</th>
                <th>Unrealized P&L</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.positions.map((p) => (
                <tr
                  key={`${p.account}-${p.ticker}-${p.secType}`}
                  className="clickable-row"
                  title={`Open ${p.ticker} chart`}
                  onClick={() => openTickerChart(p.ticker)}
                >
                  <td className="ticker-cell">
                    <strong>{p.ticker}</strong>
                    {p.secType !== 'STK' && <span className="type-badge"> {p.secType}</span>}
                  </td>
                  <td>{p.position}</td>
                  <td>${p.avgCost.toFixed(2)}</td>
                  <td>{p.mark !== null ? `$${p.mark.toFixed(2)}` : '—'}</td>
                  <td>{money(p.marketValue)}</td>
                  <td style={{ color: pnlColor(p.dayChangePct) }}>
                    {p.dayChangePct !== null
                      ? `${p.dayChangePct >= 0 ? '+' : ''}${p.dayChangePct.toFixed(2)}%`
                      : '—'}
                  </td>
                  <td style={{ color: pnlColor(p.unrealizedPnL) }}>
                    {p.unrealizedPnL !== null
                      ? `${p.unrealizedPnL >= 0 ? '+' : ''}${money(p.unrealizedPnL)} (${
                          p.unrealizedPnLPct !== null ? p.unrealizedPnLPct.toFixed(1) : '—'
                        }%)`
                      : '—'}
                  </td>
                  <td>
                    <div className="row-actions">
                      <button
                        className="buy-btn"
                        title={p.position < 0 ? `Buy to close ${p.ticker} short` : `Buy more ${p.ticker}`}
                        onClick={(e) => {
                          e.stopPropagation(); // don't also open the chart
                          setTicket({ p, action: 'BUY' });
                        }}
                      >
                        BUY
                      </button>
                      {p.position > 0 && (
                        <button
                          className="sell-btn"
                          title={`Sell ${p.ticker}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setTicket({ p, action: 'SELL' });
                          }}
                        >
                          SELL
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}

      {ticket && (
        <OrderTicket
          ticker={ticket.p.ticker}
          action={ticket.action}
          maxQty={
            ticket.action === 'SELL'
              ? ticket.p.position
              : ticket.p.position < 0
                ? Math.abs(ticket.p.position) // buy-to-close prefills the short size
                : 1 // adding to a long prefills 1
          }
          mark={ticket.p.mark}
          onClose={() => setTicket(null)}
        />
      )}
    </div>
  );
}
